import {TestObservation} from '../types';
import {
    CascadeEntry,
    buildCascades,
} from './buildCascades';
import {
    ObservationKind,
    observationKindDisplay,
} from './classifyObservation';

export type {CascadeEntry};

// mantine-datatable renders a FLAT list of records. The cascade tree therefore has
// to be flattened into rows that each remember where they sit — a child immediately
// follows its root and carries depth 1, so the indent + the muted styling in the
// columns make the group read as one unit.
//
// This is the step that keeps "one bug" true on screen: the dependents are never
// scattered away from the failure that caused them, and never sorted above it.
export type CascadeRow = {
    record: TestObservation,
    kind: ObservationKind,
    // 0 = a top-level row (a root cause, or a standalone observation).
    // 1 = a consequence, rendered underneath and subordinate to the row above it.
    depth: number,
    // Populated for a dependent (the codes it deferred to) AND for an orphan whose
    // root is not in the current view, so neither can look like a bare skip.
    blockedBy: string[],
    // Set on a root: how many actions this single failure took down. The blast
    // radius belongs on the cause, not spread across the consequences.
    childCount: number,
    muted: boolean,
};

export const flattenCascades = (entries: CascadeEntry[]): CascadeRow[] =>
    entries.flatMap((entry) => {
        const rootRow: CascadeRow = {
            record: entry.record,
            kind: entry.kind,
            depth: 0,
            blockedBy: entry.orphanedDependencies,
            childCount: entry.children.length,
            muted: observationKindDisplay(entry.kind).muted,
        };

        const childRows: CascadeRow[] = entry.children.map((child) => ({
            record: child.record,
            kind: child.kind,
            depth: 1,
            blockedBy: [entry.record.actionCode],
            childCount: 0,
            muted: true,
        }));

        return [rootRow, ...childRows];
    });

// Convenience for callers that hold raw records (the synthetic-user panel).
export const toCascadeRows = (records: TestObservation[]): CascadeRow[] =>
    flattenCascades(buildCascades(records));
